/**
 * 従業員の詳細(固有 URL の全画面)。所属・役職・上長・兼務をここから直す
 * (書き込みは hr:operator 以上)。
 *
 * 詳細テンプレート(`ui/DetailPage`): ヘッダー + サマリー帯 + タブ(基本情報 / 貸与品 / 兼務)。
 * 編集は既存の `EmployeeForm` を Drawer で出す(フォーム自体は UX1-b2 で整える)。
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Flex,
  Form,
  Input,
  Modal,
  Popconfirm,
  Spin,
  Table,
  Tag,
  TreeSelect,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import type { Concurrent, EmployeeDetail, Loan } from "../../lib/api";
import { hasFeature } from "../../lib/features";
import { useApi, useMe } from "../../lib/useApi";
import { jst } from "../../lib/format";
import { DetailPage, Initials, KeyValueList, Section, StatusTag } from "../../ui";
import { EmployeeFormModal } from "./EmployeeForm";
import { employeeStatusTone, statusLabel } from "./constants";
import { errorText, unitOptions, useCustomFields, useHrPerms, useOrgUnits } from "./hooks";
import { useEmployeeLoans } from "./loanHooks";

const SOURCE_LABEL: Record<string, string> = {
  manual: "手動指定",
  auto: "役職からの自動導出",
};

function BasicTab({ emp }: { emp: EmployeeDetail }) {
  const kana = [emp.family_name_kana, emp.given_name_kana].filter(Boolean).join(" ");
  const fields = useCustomFields().data?.items ?? [];
  return (
    <div className="ui-detail-grid">
      <div className="ui-detail-col">
        <Section title="基本情報">
          <KeyValueList
            columns={2}
            items={[
              { label: "社員番号", value: emp.emp_code, always: true },
              { label: "氏名", value: emp.name, always: true },
              { label: "カナ", value: kana },
              {
                label: "在籍状態",
                always: true,
                value: (
                  <StatusTag tone={employeeStatusTone(emp.status)}>{statusLabel(emp.status)}</StatusTag>
                ),
              },
              {
                label: "契約形態",
                value: emp.employment_type
                  ? `${emp.employment_type}${emp.direct ? "" : "(直雇用ではない)"}`
                  : "",
              },
              { label: "メール", value: emp.email },
              { label: "所属", value: emp.org_unit_path, always: true },
              {
                label: "役職",
                value: emp.position_name
                  ? `${emp.position_name}${emp.position_rank != null ? `(rank ${emp.position_rank})` : ""}`
                  : "",
              },
              {
                label: "上長",
                always: true,
                value: emp.manager_name ? (
                  <>
                    {emp.manager_name}
                    {emp.manager_source && (
                      <Tag style={{ marginLeft: 8 }}>
                        {SOURCE_LABEL[emp.manager_source] ?? emp.manager_source}
                      </Tag>
                    )}
                  </>
                ) : (
                  ""
                ),
              },
              { label: "入社日", value: emp.joined_at },
              { label: "退職日", value: emp.left_at },
              { label: "会社携帯", value: emp.work_mobile },
              { label: "会社電話", value: emp.work_phone },
              { label: "内線", value: emp.extension },
              { label: "備考", value: emp.note, wide: true },
            ]}
          />
        </Section>
        {fields.length > 0 && (
          <Section title="カスタム項目">
            <KeyValueList
              columns={2}
              items={fields.map((f) => ({ label: f.label, value: emp.extra?.[f.key] ?? "" }))}
            />
          </Section>
        )}
      </div>
      <div className="ui-detail-col">
        <Section title="記録">
          <KeyValueList
            items={[
              { label: "登録", value: jst(emp.created_at) },
              { label: "更新", value: jst(emp.updated_at) },
              { label: "匿名化", value: emp.anonymized_at ? jst(emp.anonymized_at) : "" },
            ]}
          />
        </Section>
      </div>
    </div>
  );
}

function LoansTab({ id }: { id: number }) {
  const loans = useEmployeeLoans(id);
  const openLoans = (loans.data?.items ?? []).filter((l) => l.open);
  const loanColumns: ColumnsType<Loan> = [
    { title: "品目", dataIndex: "item", width: 160 },
    {
      title: "貸与中",
      width: 90,
      render: (_: unknown, row: Loan) => (row.open ? <span data-loan-open={row.item}>◯</span> : ""),
    },
    {
      title: "管理番号",
      width: 200,
      render: (_: unknown, row: Loan) =>
        row.asset_no ? (
          <Link to={`/hr/equipment?q=${encodeURIComponent(row.asset_no)}`}>{row.asset_no}</Link>
        ) : (
          "—"
        ),
    },
    { title: "セクション", dataIndex: "section", width: 160, render: (v: string) => v || "—" },
    { title: "貸与日", width: 180, render: (_: unknown, row: Loan) => jst(row.lent_at) },
    {
      title: "返却日",
      width: 180,
      render: (_: unknown, row: Loan) =>
        row.returned_at ? jst(row.returned_at) : <Tag color="gold">貸与中</Tag>,
    },
    {
      title: "申請",
      width: 180,
      render: (_: unknown, row: Loan) => (
        <span>
          {row.request_id ? <Link to={`/hr/wf-requests/${row.request_id}`}>#{row.request_id}</Link> : "—"}
          {row.return_request_id ? (
            <>
              {" → "}
              <Link to={`/hr/wf-requests/${row.return_request_id}`}>#{row.return_request_id}</Link>
            </>
          ) : null}
        </span>
      ),
    },
  ];
  return (
    <Section title="貸与品" flush>
      <Table<Loan>
        rowKey="id"
        size="small"
        columns={loanColumns}
        dataSource={loans.data?.items ?? []}
        loading={loans.isFetching}
        pagination={false}
        scroll={{ x: "max-content" }}
        locale={{ emptyText: "貸与品なし" }}
      />
      <Typography.Paragraph type="secondary" style={{ fontSize: 12, margin: 0, padding: "12px 20px" }}>
        貸与中: {openLoans.length ? openLoans.map((l) => l.item).join("、") : "なし"}(
        <Link to="/hr/loans">貸与品台帳</Link>)。台帳が動くのは貸与品の工程を完了したときと、
        備品マスタからの即時貸出・返却だけ。
      </Typography.Paragraph>
    </Section>
  );
}

function ConcurrentTab({ emp, canWrite }: { emp: EmployeeDetail; canWrite: boolean }) {
  const api = useApi();
  const qc = useQueryClient();
  const units = useOrgUnits(true);
  const [adding, setAdding] = useState(false);
  const [form] = Form.useForm<{ unit_id: number; label?: string }>();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ["hr"] });
    void qc.invalidateQueries({ queryKey: ["audit"] });
  };
  const addConcurrent = useMutation({
    mutationFn: (v: { unit_id: number; label?: string }) =>
      api.addConcurrent(emp.id, { unit_id: v.unit_id, label: v.label ?? "" }),
    onSuccess: () => {
      void message.success("兼務を追加した");
      setAdding(false);
      form.resetFields();
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });
  const removeConcurrent = useMutation({
    mutationFn: (concurrentId: number) => api.deleteConcurrent(emp.id, concurrentId),
    onSuccess: () => {
      void message.success("兼務を外した");
      invalidate();
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const columns: ColumnsType<Concurrent> = [
    { title: "組織", width: 320, dataIndex: "unit_path", ellipsis: true },
    { title: "ラベル", dataIndex: "label", width: 160 },
    {
      title: "状態",
      dataIndex: "active",
      width: 110,
      render: (v: boolean) =>
        v ? <StatusTag tone="success">稼働中</StatusTag> : <StatusTag tone="neutral">停止中</StatusTag>,
    },
    ...(canWrite
      ? [
          {
            title: "",
            width: 90,
            render: (_: unknown, row: Concurrent) => (
              <Popconfirm
                title="この兼務を外す"
                okText="外す"
                cancelText="やめる"
                onConfirm={() => removeConcurrent.mutate(row.id)}
              >
                <Button size="small" danger>
                  外す
                </Button>
              </Popconfirm>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <Section
        title="兼務(主所属以外の組織)"
        flush
        extra={
          canWrite ? (
            <Button size="small" onClick={() => setAdding(true)}>
              兼務を追加
            </Button>
          ) : null
        }
      >
        <Table<Concurrent>
          scroll={{ x: "max-content" }}
          rowKey="id"
          size="small"
          columns={columns}
          dataSource={emp.concurrent}
          pagination={false}
          locale={{ emptyText: "兼務なし" }}
        />
      </Section>
      <Modal
        open={adding}
        title="兼務を追加"
        okText="追加"
        cancelText="やめる"
        confirmLoading={addConcurrent.isPending}
        onCancel={() => setAdding(false)}
        onOk={() => {
          void form.validateFields().then((v) => addConcurrent.mutate(v));
        }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="unit_id" label="組織" rules={[{ required: true, message: "組織を選ぶ" }]}>
            <TreeSelect treeDefaultExpandAll treeData={unitOptions(units.data?.items ?? [])} />
          </Form.Item>
          <Form.Item name="label" label="ラベル" extra="空なら「メンバー」。組織図の見出しになる">
            <Input maxLength={40} placeholder="兼務 / 応援 など" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

export function EmployeeDetailPage() {
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { employeeId } = useParams();
  const id = Number(employeeId);
  const { canWrite, canAdmin } = useHrPerms();
  const me = useMe();
  // 物理削除の API は匿名化と同じ機能(hr.anonymize)の中。未契約だと 403 になるので出さない
  const canDeleteFeature = hasFeature(me.data, "hr.anonymize");
  const [editing, setEditing] = useState(false);

  const query = useQuery({
    queryKey: ["hr", "employee", id],
    queryFn: () => api.employee(id),
    enabled: Number.isFinite(id),
  });
  const loans = useEmployeeLoans(Number.isFinite(id) ? id : undefined);

  const anonymize = useMutation({
    mutationFn: () => api.anonymizeEmployee(id),
    onSuccess: (res) => {
      void message.success(`匿名化した(履歴の置換 ${res.history_rows} 行)`);
      void qc.invalidateQueries({ queryKey: ["hr"] });
      void qc.invalidateQueries({ queryKey: ["audit"] });
    },
    onError: (e) => void message.error(errorText(e)),
  });

  const remove = useMutation({
    mutationFn: () => api.deleteEmployee(id),
    onSuccess: (res) => {
      void message.success(`削除した(${res.emp_code})`);
      // 消した本人の詳細は取り直さない(404 になる)。一覧などは取り直す
      qc.removeQueries({ queryKey: ["hr", "employee", id] });
      void qc.invalidateQueries({
        queryKey: ["hr"],
        predicate: (q) => !(q.queryKey[1] === "employee" && q.queryKey[2] === id),
      });
      void qc.invalidateQueries({ queryKey: ["audit"] });
      navigate("/hr/employees");
    },
    onError: (e: unknown) => {
      // 409 の案内(どの履歴が残っているか)はサーバの文をそのまま見せる
      Modal.error({ title: "削除できない", content: errorText(e), okText: "閉じる" });
    },
  });

  if (query.isLoading)
    return (
      <Flex justify="center" style={{ padding: 64 }}>
        <Spin />
      </Flex>
    );
  if (query.isError)
    return (
      <Alert type="error" showIcon message="従業員を取得できなかった" description={errorText(query.error)} />
    );

  const emp = query.data!;
  const openLoans = (loans.data?.items ?? []).filter((l) => l.open);

  return (
    <>
      <DetailPage
        back={{ to: "/hr/employees", label: "従業員一覧へ戻る" }}
        avatar={<Initials name={emp.name} size="lg" />}
        title={emp.name}
        tags={
          <>
            <StatusTag tone={employeeStatusTone(emp.status)}>{statusLabel(emp.status)}</StatusTag>
            {emp.name_needs_review && <StatusTag tone="warning">氏名が要確認</StatusTag>}
            {emp.anonymized_at && <StatusTag tone="neutral">匿名化済み</StatusTag>}
          </>
        }
        sub={
          <>
            <span className="ui-num">{emp.emp_code}</span>
            {emp.email && <span>{emp.email}</span>}
            {emp.employment_type && <span>{emp.employment_type}</span>}
          </>
        }
        actions={
          <>
            {canWrite && (
              <Button type="primary" onClick={() => setEditing(true)}>
                編集
              </Button>
            )}
            {/* 匿名化は退職済みだけ・hr:admin だけ。押すと元に戻せない */}
            {canAdmin && emp.status === "offboarded" && emp.anonymized_at === null && (
              <Popconfirm
                title="この従業員を匿名化する"
                description={
                  <span>
                    氏名・メールアドレス・社員番号・所属・役職・上長・契約形態・電話を消す。
                    <br />
                    申請・タスク・貸与・監査の行は残し、その中の文字列も置き換える。
                    <br />
                    元には戻せない。
                  </span>
                }
                okText="匿名化する"
                okButtonProps={{ danger: true, loading: anonymize.isPending }}
                cancelText="やめる"
                onConfirm={() => anonymize.mutate()}
              >
                <Button danger>匿名化</Button>
              </Popconfirm>
            )}
            {/* 削除は入社前で履歴が無いときだけ・hr:admin だけ(取り下げで登録を残した人を消す口) */}
            {canAdmin && canDeleteFeature && emp.deletable && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                入社前で履歴が無いときだけ
              </Typography.Text>
            )}
            {canAdmin && canDeleteFeature && emp.deletable && (
              <Button
                danger
                loading={remove.isPending}
                onClick={() =>
                  Modal.confirm({
                    title: `${emp.name}(${emp.emp_code})を削除する`,
                    okText: "削除する",
                    okButtonProps: { danger: true },
                    cancelText: "やめる",
                    content: (
                      <Flex vertical gap={4}>
                        <span>入社前で履歴が無いので、登録そのものを消す。</span>
                        <Typography.Text type="danger">元には戻せない。</Typography.Text>
                        <Typography.Text type="secondary">
                          入社申請を出し直すときは、新しい申請 › 入社申請で改めて入力する。
                        </Typography.Text>
                      </Flex>
                    ),
                    onOk: () => remove.mutateAsync(),
                  })
                }
              >
                削除
              </Button>
            )}
          </>
        }
        alert={
          emp.manager_warning ? (
            <Alert type="warning" showIcon message="上長の指定に問題がある" description={emp.manager_warning} />
          ) : undefined
        }
        summary={[
          { label: "所属", value: emp.org_unit_path, empty: "未所属" },
          { label: "役職", value: emp.position_name },
          {
            label: "在籍",
            value: (
              <StatusTag tone={employeeStatusTone(emp.status)} dot>
                {statusLabel(emp.status)}
              </StatusTag>
            ),
          },
          { label: "入社日", value: emp.joined_at },
          { label: "上長", value: emp.manager_name, empty: "なし" },
        ]}
        tabs={[
          { key: "basic", label: "基本情報", children: <BasicTab emp={emp} /> },
          { key: "loans", label: "貸与品", badge: openLoans.length, children: <LoansTab id={id} /> },
          {
            key: "concurrent",
            label: "兼務",
            badge: emp.concurrent.length,
            children: <ConcurrentTab emp={emp} canWrite={canWrite} />,
          },
        ]}
      />
      <EmployeeFormModal as="drawer" open={editing} employee={emp} onClose={() => setEditing(false)} />
    </>
  );
}
